/**
 * skillCommissioningService — authFetch wrappers around /api/admin/skills/.
 *
 * WQ-245 (Phase 4.10 β10). Consumes the 2 endpoints shipped by
 * skill_commissioning_router (sole permitted caller of
 * SkillLibraryService.set_enabled() per Decision 1).
 *
 *   GET  /api/admin/skills
 *   POST /api/admin/skills/{slug}/commission
 *
 * Both endpoints require SYSTEM_ADMIN permission (super_admin operator).
 */

export interface SkillManifestSummary {
  performance_baselines_present: boolean;
  contract_tests_count: number;
  laws_compliance_ref: string | null;
  drift_monitoring_present: boolean;
  drift_metrics_count: number;
}

export interface SkillSummary {
  id: string;
  slug: string;
  name: string;
  version: string;
  contract_version: string | null;
  integrity_level: string | null;
  tier: string | null;
  enabled: boolean;
  is_active: boolean;
  license_status: string | null;
  manifest_summary: SkillManifestSummary | null;
}

export interface CommissionResponse {
  slug: string;
  enabled: boolean;
  rationale_recorded: boolean;
  gate_name: string;
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/skills';

export async function listSkills(authFetch: AuthFetch): Promise<SkillSummary[]> {
  const res = await authFetch(BASE);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`listSkills failed: ${res.status} ${detail}`);
  }
  return res.json();
}

export async function commissionSkill(
  authFetch: AuthFetch,
  slug: string,
  enabled: boolean,
  rationale: string,
): Promise<CommissionResponse> {
  const res = await authFetch(`${BASE}/${slug}/commission`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled, rationale }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`commissionSkill failed: ${res.status} ${detail}`);
  }
  return res.json();
}

// ============================================================================
// WQ-263 — Obligation Evaluator endpoint client.
// ============================================================================

export type ObligationResult = 'pass' | 'fail' | 'hold' | 'n_a';
export type SubitemResultValue =
  | 'pass'
  | 'fail'
  | 'hold'
  | 'n_a'
  | 'pending_first_invocation';

export interface SubitemResult {
  subitem_number: number;
  description: string;
  result: SubitemResultValue;
  evidence_pointer?: string | null;
  notes?: string | null;
}

export interface ObligationEvaluation {
  obligation_number: number;
  obligation_name: string;
  result: ObligationResult;
  subitem_results: SubitemResult[];
  evaluator_notes?: string;
}

export interface ObligationEvaluatorResponse {
  skill_slug: string;
  obligations: ObligationEvaluation[];
  aggregate_result: 'authorize' | 'revise' | 'hold';
  counts: {
    pass: number;
    pass_with_notes: number;
    fail: number;
    hold: number;
    n_a: number;
  };
  most_recent_review_record_path?: string | null;
  most_recent_review_outcome?: string | null;
  most_recent_review_date?: string | null;
}

/** WQ-263 Scope B.1 — fetch per-obligation Pass/Fail/Hold/N/A + per-subitem
 * detail for the SkillCommissioningPage modal. Replaces the WQ-245 modal's
 * JSON-shape presence checks + hardcoded ✅ marks with real per-obligation
 * review status from the backend evaluator. */
export async function getSkillObligations(
  authFetch: AuthFetch,
  slug: string,
): Promise<ObligationEvaluatorResponse> {
  const res = await authFetch(`${BASE}/${slug}/obligations`);
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`getSkillObligations failed: ${res.status} ${detail}`);
  }
  return res.json();
}
